import EditIcon from "@mui/icons-material/Edit";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  MenuItem,
} from "@mui/material";
import { useState } from "react";
import { trackAction } from "#next/utils/trackUserActions";
import { trpc } from "#next/utils/trpc";
import { Link } from "#next/components/reusable/Link";
import { ManageSubscriptionsModal } from "#next/components/modals/ManageSubscriptionsModal";
import { RenderQuery } from "#next/components/base/RenderQuery";
import {
  useCheckIfCurrentRoute,
  highlightColor,
  NavigationLink,
} from "#next/components/navigation/shared";

const SavedSearchLink = ({
  savedSearchId,
  name,
  editable,
  onSelect,
}: {
  savedSearchId: string;
  name: string;
  editable: boolean;
  onSelect?: () => void;
}) => {
  const isCurrentProject = useCheckIfCurrentRoute("explorer", savedSearchId);
  const isEditingCurrentProject = useCheckIfCurrentRoute(
    "projectEditor",
    savedSearchId,
  );
  const nameString = editable ? name : `${name} (shared)`;

  return (
    <ListItem
      sx={{
        backgroundColor: isCurrentProject ? highlightColor : undefined,
        alignItems: "inherit",
        pt: 1,
      }}
    >
      <ListItemText
        sx={{
          mt: 0,
          mb: 0,
          "&:hover .MuiTypography-root": { color: highlightColor },
        }}
      >
        <Link
          onClick={() => {
            trackAction("Explorer > Open");
            onSelect?.();
          }}
          href={{
            pathname: "/explorer",
            query: { projectId: savedSearchId, tr: "7d" },
          }}
        >
          {nameString}
        </Link>
      </ListItemText>
      {editable && (
        <ListItemIcon sx={{ display: "flex", justifyContent: "end" }}>
          <Link
            onClick={() => {
              trackAction("Project Editor > Open (Edit)");
              onSelect?.();
            }}
            href={{
              pathname: "/project-editor",
              query: { projectId: savedSearchId },
            }}
            sx={{
              "&:hover .MuiSvgIcon-root": { color: highlightColor },
              "& .MuiSvgIcon-root": {
                color: isEditingCurrentProject
                  ? highlightColor // TODO: Find a better colour or style?
                  : undefined,
              },
            }}
          >
            <EditIcon sx={{ fontSize: "1rem" }} />
          </Link>
        </ListItemIcon>
      )}
    </ListItem>
  );
};

const UsernameSearchLink = ({
  username,
  platform,
  onSelect,
}: {
  username: string;
  platform: string;
  onSelect?: () => void;
}) => {
  const isCurrentSearch = useCheckIfCurrentRoute(
    "userSearch",
    undefined,
    username,
    platform,
  );
  const nameString = `${username} (on ${platform})`;

  return (
    <ListItem
      sx={{
        backgroundColor: isCurrentSearch ? highlightColor : undefined,
        alignItems: "inherit",
        pt: 1,
      }}
    >
      <ListItemText
        sx={{
          mt: 0,
          mb: 0,
          "&:hover .MuiTypography-root": { color: highlightColor },
        }}
      >
        <Link
          onClick={() => {
            trackAction("User Search > Open");
            onSelect?.();
          }}
          href={{
            pathname: "/user-search",
            query: { username: username, platform: platform },
          }}
        >
          {nameString}
        </Link>
      </ListItemText>
    </ListItem>
  );
};

const SubscriptionsLink = () => {
  const [subscriptionsModalIsOpen, setSubscriptionsModalIsOpen] =
    useState(false);

  const linkStyle = {
    "&:hover": {
      color: subscriptionsModalIsOpen ? undefined : highlightColor,
    },
  };

  return (
    <>
      <MenuItem
        sx={{
          ...linkStyle,
          py: 0,
          backgroundColor: subscriptionsModalIsOpen
            ? highlightColor
            : undefined,
        }}
        onClick={() => {
          trackAction("My Subscriptions > Open");
          setSubscriptionsModalIsOpen(true);
        }}
      >
        <ListItemText>Manage Subscriptions</ListItemText>
      </MenuItem>
      {subscriptionsModalIsOpen && (
        <ManageSubscriptionsModal
          open={subscriptionsModalIsOpen}
          onClose={() => setSubscriptionsModalIsOpen(false)}
        />
      )}
    </>
  );
};

export const SavedSearchList = ({ onSelect }: { onSelect?: () => void }) => {
  return (
    <RenderQuery
      useQuery={trpc.savedSearch.listSidebar}
      options={{ trpc: { context: { skipBatch: true } } }}
      render={({ recentSearches, newSearches, recentUserSearches }) => (
        <>
          <List dense sx={{ pt: 2 }}>
            <NavigationLink
              location="catalog"
              name="Manage Projects"
              actionName="Catalog > Open"
              onSelect={onSelect}
            />
            <SubscriptionsLink />
            <NavigationLink
              location="projectEditor"
              name="Create New Project"
              actionName="Project Editor > Open (Create)"
              onSelect={onSelect}
            />
            <ListItem sx={{ mt: 2 }}>
              <ListItemText>
                <Typography variant="h4">Recent Projects</Typography>
              </ListItemText>
            </ListItem>
            {recentSearches.length > 0 ? (
              <>
                {recentSearches.map(({ name, id, editable }) => (
                  <SavedSearchLink
                    savedSearchId={id}
                    name={name}
                    key={id}
                    editable={editable}
                    onSelect={onSelect}
                  />
                ))}
              </>
            ) : (
              <ListItem
                sx={{
                  alignItems: "inherit",
                  pt: 1,
                }}
              >
                <ListItemText
                  sx={{
                    mt: 0,
                    mb: 0,
                  }}
                >
                  No recent projects!
                </ListItemText>
              </ListItem>
            )}
            <ListItem sx={{ mt: 3 }}>
              <ListItemText>
                <Typography variant="h4">New Projects</Typography>
              </ListItemText>
            </ListItem>
            {newSearches.length > 0 ? (
              <>
                {newSearches.map(({ name, id, editable }) => (
                  <SavedSearchLink
                    savedSearchId={id}
                    name={name}
                    key={id}
                    editable={editable}
                    onSelect={onSelect}
                  />
                ))}
              </>
            ) : (
              <ListItem
                sx={{
                  alignItems: "inherit",
                  pt: 1,
                }}
              >
                <ListItemText
                  sx={{
                    mt: 0,
                    mb: 0,
                  }}
                >
                  No new projects!
                </ListItemText>
              </ListItem>
            )}
            <ListItem sx={{ mt: 3 }}>
              <ListItemText>
                <Typography variant="h4">User Searches</Typography>
              </ListItemText>
            </ListItem>
            {recentUserSearches.length > 0 ? (
              <>
                {recentUserSearches.map(({ username, platform }, index) => (
                  <UsernameSearchLink
                    username={username}
                    key={index}
                    platform={platform}
                    onSelect={onSelect}
                  />
                ))}
              </>
            ) : (
              <ListItem
                sx={{
                  alignItems: "inherit",
                  pt: 1,
                }}
              >
                <ListItemText
                  sx={{
                    mt: 0,
                    mb: 0,
                  }}
                >
                  No recent user searches!
                </ListItemText>
              </ListItem>
            )}
          </List>
        </>
      )}
    />
  );
};
